import { useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { Furnace } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Pagination from "../../common/Pagination";
import Model from "../../common/Model";
import FormFields from "../../common/FormFields";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import Button from "../../common/Button";

const FurnaceMaster = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    furnaceId: 0,
    furnaceName: "",
    burnerType: "",
    metalBathCapacity: "",
    burnerCapacity: "",
    fuelSource: "",
    castingTable: "",
    castingPitLength: "",
    castingPitWidth: "",
    castingPitDepth: "",
    mainTankCapacity: "",
    serviceTankCapacity: "",
    moldSize: "",
    createdBy: userId,
  });
  const [loading, setLoading] = useState(false);
  const [isFurnaceData, setIsFurnaceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);

  const fetchFurnaceData = async () => {
    setLoading(true);
    try {
      const res = await Furnace.getAllFurnace(currentPage, pageSize);
      const newItems = res.data;

      if (currentPage === 1) {
        setIsFurnaceData(newItems);
      } else {
        setIsFurnaceData((prev) => [...prev, ...newItems]);
      }

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notify("Error fetching location data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchFurnaceData();
  }, [currentPage]);

  const fuelSourceOptions = [
    { value: "GAS", label: "Gas" },
    { value: "OIL", label: "Oil" },
    { value: "ELECTRICAL", label: "Electrical" },
  ];

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(isFurnaceData);
    } else {
      const filtered = isFurnaceData.filter((d) =>
        d.furnaceName?.toLowerCase()?.includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, isFurnaceData]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const finalValue = type === "number" ? (value === "" ? "" : Number(value)) : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData({
      furnaceId: 0,
      furnaceName: "",
      burnerType: "",
      burnerCapacity: "",
      metalBathCapacity: "",
      fuelSource: "",
      castingTable: "",
      castingPitLength: "",
      castingPitWidth: "",
      castingPitDepth: "",
      mainTankCapacity: "",
      serviceTankCapacity: "",
      moldSize: "",
      createdBy: userId,
    });
  };

  const handleEdit = async (id) => {
    try {
      const res = await Furnace.getById(id);
      const data = res?.data;
      if (data) {
        setFormData({
          furnaceId: data.furnaceId,
          furnaceName: data.furnaceName,
          metalBathCapacity: data.metalBathCapacity ?? "",
          burnerType: data.burnerType ?? "",
          burnerCapacity: data.burnerCapacity ?? "",
          fuelSource: data.fuelSource ?? "",
          castingTable: data.castingTable ?? "",
          castingPitLength: data.castingPitLength ?? "",
          castingPitWidth: data.castingPitWidth ?? "",
          castingPitDepth: data.castingPitDepth ?? "",
          mainTankCapacity: data.mainTankCapacity ?? "",
          serviceTankCapacity: data.serviceTankCapacity ?? "",
          moldSize: data.moldSize ?? "",
          createdBy: userId,
        });

        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch furnace details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await Furnace.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchFurnaceData();
    } catch (error) {
      notify("Failed to delete record", "error");
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleSubmit = async ({ resetOnly = false }) => {
    setLoading(true);
    try {
      await Furnace.saveOrUpdate(formData);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success",
      );
      fetchFurnaceData();
      setFormData({
        furnaceId: 0,
        furnaceName: "",
        burnerType: "",
        burnerCapacity: "",
        metalBathCapacity: "",
        fuelSource: "",
        castingTable: "",
        castingPitLength: "",
        castingPitWidth: "",
        castingPitDepth: "",
        mainTankCapacity: "",
        serviceTankCapacity: "",
        moldSize: "",
        createdBy: userId,
      });
      setIsEditing(false);
      if (!resetOnly) {
        setIsModelOpen(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    {
      name: "furnaceName",
      label: "Furnace Name",
      type: "text",
      required: true,
    },
    {
      name: "metalBathCapacity",
      label: "Molten Metal Bath Capacity",
      type: "number",
      unit: "KG",
    },
    {
      name: "burnerType",
      label: "Burner Type",
      type: "text",
    },
    {
      name: "burnerCapacity",
      label: "Burner Capacity",
      type: "number",
      unit: "Kcal",
    },
    {
      name: "fuelSource",
      label: "Fuel Source",
      type: "select",
      options: fuelSourceOptions,
      grid: "col-span-12 sm:col-span-6",
    },
    ...(formData.fuelSource === "OIL"
      ? [
        {
          name: "mainTankCapacity",
          label: "Main Tank Cap.",
          type: "number",
          unit: "/Inches",
          grid: "col-span-12 sm:col-span-3",
        },
        {
          name: "serviceTankCapacity",
          label: "Service Tank Cap.",
          type: "number",
          unit: "/Inches",
          grid: "col-span-12 sm:col-span-3",
        },
      ]
      : []),
    {
      name: "castingTable",
      label: "Casting Table (No. of Molds)",
      type: "number",
    },
    {
      name: "moldSize",
      label: "Mold Size",
      type: "number",
      unit: "MM",
    },
    {
      name: "castingPitLength",
      label: "Casting Pit Length",
      type: "number",
      unit: "MM",
    },
    {
      name: "castingPitWidth",
      label: "Casting Pit Width",
      type: "number",
      unit: "MM",
    },
    {
      name: "castingPitDepth",
      label: "Casting Pit Depth",
      type: "number",
      unit: "MM",
    },
  ];

  return (
    <>
      <div>
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col">
            <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">
              Furnace Master
            </h2>
            <p className="text-sm text-gray-500">
              Manage and track your furnace inventory
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-72 group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LiaSearchSolid className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Search by Furnace Name..."
                className="block w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-red-500 transition-colors"
                >
                  <MdClose size={18} />
                </button>
              )}
            </div>

            <Button
              text="Add Furnace"
              className="bg-orange text-black hover:bg-white font-medium px-4 py-2 rounded-lg flex items-center shadow-sm transition-all"
              icon={HiOutlinePlus}
              onClick={handleAdd}
            />
          </div>
        </div>

        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-slate-200 rounded-xl shadow-inner bg-white"
          style={{ height: "calc(100vh - 320px)" }}
        >
          <div className="min-w-max">
            {loading && <Loader />}
            <Table
              columns={[
                { key: "furnaceName", label: "Name" },
                { key: "metalBathCapacity", label: "Bath Capacity (KG)" },
                { key: "burnerType", label: "Burner Type" },
                { key: "burnerCapacity", label: "Burner Capacity (Kcal)" },
                { key: "fuelSource", label: "Fuel" },
                { key: "mainTankCapacity", label: "Main Tank Cap. (/Inches)" },
                { key: "serviceTankCapacity", label: "Service Tank Cap. (/Inches)" },
                { key: "castingTable", label: "Casting Molds" },
                { key: "moldSize", label: "Mold Size" },
                { key: "castingPitLength", label: "Pit L (MM)" },
                { key: "castingPitWidth", label: "Pit W (MM)" },
                { key: "castingPitDepth", label: "Pit D (MM)" },
              ]}
              data={filteredData}
              onEdit={(row) => handleEdit(row.furnaceId)}
              onDelete={(row) => handleDelete(row.furnaceId)}
            />
          </div>
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Furnace " : "Add Furnace "}
      >
        <FormFields
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: false });
          }}
          onSubmitAndAddNew={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: true });
          }}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
          loading={loading}
        />
      </Model>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default FurnaceMaster;
